import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  BillingSettings,
  BillingMonthlyStats,
  BillingYearlyStats,
  BillingAggregatedStats,
} from "../../settings/settings.interface";

@Injectable({
  providedIn: "root",
})
export class BillingSettingsService {
  constructor(private http: HttpClient) {}

  get(): Observable<BillingSettings> {
    return this.http.get<BillingSettings>("/billing-settings");
  }

  update(
    payload: BillingSettings,
    params?: HttpParams,
  ): Observable<BillingSettings> {
    return this.http.post<BillingSettings>("/billing-settings", payload, {
      params,
    });
  }

  savePendingPlanChange(payload: {
    planName: string;
    subscriptionPeriod: string;
    currency: string;
    price: number;
  }): Observable<BillingSettings> {
    return this.http.post<BillingSettings>(
      "/billing-settings/pending-plan-change",
      payload,
    );
  }

  cancelPendingPlanChange(): Observable<BillingSettings> {
    return this.http.delete<BillingSettings>(
      "/billing-settings/pending-plan-change",
    );
  }

  updatePaymentMethod(paymentMethod: string): Observable<unknown> {
    return this.http.post("/billing-settings/payment-method", {
      paymentMethod,
    });
  }

  getAllDomains(): Observable<string[]> {
    return this.http.get<string[]>("/admin/client/all-domains");
  }

  getMonthlyStats(domain: string): Observable<BillingMonthlyStats[]> {
    return this.http.get<BillingMonthlyStats[]>(
      `/billing-report/monthly/${domain}`,
    );
  }

  getAggregatedMonthlyStats(
    domain: string,
  ): Observable<BillingAggregatedStats[]> {
    return this.http.get<BillingAggregatedStats[]>(
      `/billing-report/aggregated/${domain}`,
    );
  }

  getYearlyStats(domain: string): Observable<BillingYearlyStats[]> {
    return this.http.get<BillingYearlyStats[]>(
      `/billing-report/yearly/${domain}`,
    );
  }

  validateBillingInfo(): Observable<{
    success: boolean;
    missingFields?: string[];
    isFreemiumPlan?: boolean;
  }> {
    return this.http.get<{
      success: boolean;
      missingFields?: string[];
      isFreemiumPlan?: boolean;
    }>("/billing-settings/validate-billing-info");
  }

  createCheckoutSession(
    flowType: "billing" | "expired" | "yearly" = "billing",
    numberOfEmployees?: number,
  ): Observable<{ url: string }> {
    const body: Record<string, unknown> = { flowType };
    if (numberOfEmployees !== undefined) {
      body["numberOfEmployees"] = numberOfEmployees;
    }
    return this.http.post<{ url: string }>(
      `/stripe/create-checkout-session`,
      body,
    );
  }

  purchaseAdditionalLicenses(
    numberOfEmployees: number,
  ): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(
      `/stripe/purchase-additional-licenses`,
      { numberOfEmployees },
    );
  }

  createUpfrontCheckoutSession(
    numberOfEmployees: number,
    subscriptionPeriod?: string,
  ): Observable<{ url: string }> {
    const interval = subscriptionPeriod === "yearly" ? "year" : "month";
    const successUrl = `${window.location.origin}/settings/billing/success`;
    const cancelUrl = `${window.location.origin}/settings/billing/cancel`;

    return this.http
      .post<{ success: boolean; url: string }>(
        `/billing/upfront-subscription/create-checkout-session`,
        {
          licenseCount: numberOfEmployees,
          interval,
          successUrl,
          cancelUrl,
        },
      )
      .pipe(map((response) => ({ url: response.url })));
  }

  createUpfrontPortalSession(returnUrl: string): Observable<{ url: string }> {
    return this.http
      .post<{
        success: boolean;
        url: string;
      }>(`/billing/upfront-subscription/create-portal-session`, { returnUrl })
      .pipe(map((response) => ({ url: response.url })));
  }

  increaseUpfrontLicenses(
    targetLicenseCount: number,
  ): Observable<{ url: string }> {
    const successUrl = `${window.location.origin}/settings/billing/success`;
    const cancelUrl = `${window.location.origin}/settings/billing/cancel`;

    return this.http
      .post<{ success: boolean; url: string }>(
        `/billing/upfront-subscription/create-increase-licenses-checkout-session`,
        {
          targetLicenseCount,
          successUrl,
          cancelUrl,
        },
      )
      .pipe(map((response) => ({ url: response.url })));
  }

  setPendingLicenseCount(
    newLicenseCount: number,
  ): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `/billing/upfront-subscription/set-pending-license-count`,
      { newLicenseCount },
    );
  }

  cancelPendingLicenseChange(): Observable<{
    success: boolean;
    message: string;
  }> {
    return this.http.post<{ success: boolean; message: string }>(
      `/billing/upfront-subscription/cancel-pending-license-change`,
      {},
    );
  }

  scheduleSubscriptionChanges(changes: {
    newLicenseCount?: number;
    newPrice?: number;
  }): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `/billing/upfront-subscription/schedule-changes`,
      changes,
    );
  }

  createFailedPaymentCheckoutSession(
    stripeLogId: string,
  ): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(
      `/stripe/create-failed-payment-checkout-session`,
      { stripeLogId },
    );
  }

  runManualBillingReport(type?: "monthly" | "yearly"): Observable<unknown> {
    return this.http.post("/billing-settings/manual-billing-report", { type });
  }

  runStripeCron(): Observable<unknown> {
    return this.http.post("/stripe/cron/manual", {});
  }
}
